import { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import Menu from "@mui/material/Menu";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useAuth } from "contexts/AuthContext";
import { paymentService } from "services/firestoreService";

function RentCollection() {
  const { user } = useAuth();
  const [rentData, setRentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [property, setProperty] = useState("all");
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    if (!user) return;
    const fetchPayments = async () => {
      try {
        const data = await paymentService.getAll(user.uid);
        setRentData(data);
      } catch (err) {
        console.error("Failed to fetch payments:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, [user]);

  const collectionStats = {
    totalUnits: rentData.length,
    collected: rentData.filter(r => r.status === 'paid').length,
    pending: rentData.filter(r => r.status === 'pending').length,
    overdue: rentData.filter(r => r.status === 'overdue' || r.status === 'late').length,
    totalAmount: rentData.reduce((sum, r) => sum + (r.amount || 0), 0),
    collectedAmount: rentData.filter(r => r.status === 'paid').reduce((sum, r) => sum + (r.amount || 0), 0),
  };
  
  const handleMenuClick = (event, tenant) => {
    setAnchorEl(event.currentTarget);
    setSelectedTenant(tenant);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleAction = (action) => {
    // Demo functionality only
    console.log(`Action ${action} for tenant ${selectedTenant?.tenant}`);
    handleMenuClose();
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "paid":
        return <Chip 
          icon={<CheckCircleIcon fontSize="small" />}
          label="Paid" 
          color="success" 
          size="small" 
          sx={{ minWidth: 85 }}
        />;
      case "pending":
        return <Chip 
          icon={<WarningIcon fontSize="small" />}
          label="Pending" 
          color="warning" 
          size="small" 
          sx={{ minWidth: 85 }}
        />;
      case "late":
        return <Chip 
          icon={<WarningIcon fontSize="small" />}
          label="Late" 
          color="warning" 
          variant="outlined"
          size="small"
          sx={{ minWidth: 85 }}
        />;
      case "overdue":
        return <Chip 
          icon={<ErrorIcon fontSize="small" />}
          label="Overdue" 
          color="error" 
          size="small"
          sx={{ minWidth: 85 }}
        />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // Filter the rent data based on selected filters and search
  const filteredData = rentData.filter((item) => {
    // Filter by status
    if (filter !== "all" && item.status !== filter) {
      return false;
    }
    
    // Filter by property
    if (property !== "all" && item.property !== property) {
      return false;
    }
    
    // Filter by search term
    if (search && !item.tenant.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Calculate collection percentage
  const collectionPercentage = Math.round((collectionStats.collectedAmount / collectionStats.totalAmount) * 100);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: "100%" }}>
                  <ArgonBox p={3}>
                    <ArgonTypography variant="subtitle2" color="text">
                      Collection Progress
                    </ArgonTypography>
                    <ArgonBox mt={2}>
                      <ArgonTypography variant="h4">
                        {collectionPercentage}%
                      </ArgonTypography>
                      <LinearProgress 
                        variant="determinate" 
                        value={collectionPercentage} 
                        color="success"
                        sx={{ height: 10, borderRadius: 5, mt: 1, mb: 1 }}
                      />
                      <ArgonTypography variant="body2" color="text">
                        ${collectionStats.collectedAmount.toLocaleString()} of ${collectionStats.totalAmount.toLocaleString()}
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </Card>
              </Grid>
              <Grid item xs={6} md={6} lg={3}>
                <Card sx={{ height: "100%" }}>
                  <ArgonBox p={3}>
                    <ArgonTypography variant="subtitle2" color="text">
                      Total Units
                    </ArgonTypography>
                    <Grid container spacing={1} mt={1}>
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1, bgcolor: "background.default", textAlign: "center" }}>
                          <Typography variant="h5" color="success.main">{collectionStats.collected}</Typography>
                          <Typography variant="caption">Paid</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1, bgcolor: "background.default", textAlign: "center" }}>
                          <Typography variant="h5" color="warning.main">{collectionStats.pending}</Typography>
                          <Typography variant="caption">Pending</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1, bgcolor: "background.default", textAlign: "center" }}>
                          <Typography variant="h5" color="error.main">{collectionStats.overdue}</Typography>
                          <Typography variant="caption">Overdue</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1, bgcolor: "background.default", textAlign: "center" }}>
                          <Typography variant="h5">{collectionStats.totalUnits}</Typography>
                          <Typography variant="caption">Total</Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </ArgonBox>
                </Card>
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <Card sx={{ height: "100%" }}>
                  <ArgonBox p={3}>
                    <ArgonTypography variant="subtitle2" color="text">
                      Quick Actions
                    </ArgonTypography>
                    <ArgonBox mt={2} display="flex" gap={2}>
                      <ArgonButton color="info" size="small">
                        Send Reminders
                      </ArgonButton>
                      <ArgonButton color="success" size="small">
                        Export Report
                      </ArgonButton>
                      <ArgonButton color="dark" size="small">
                        Payment History
                      </ArgonButton>
                    </ArgonBox>
                  </ArgonBox>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Filters and Table */}
          <Grid item xs={12}>
            <Card>
              <ArgonBox p={3}>
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <ArgonTypography variant="h5" fontWeight="medium">
                    Rent Collection
                  </ArgonTypography>
                  <ArgonButton color="primary" size="small">
                    + Record Payment
                  </ArgonButton>
                </ArgonBox>
                
                {/* Filters */}
                <ArgonBox mb={3} display="flex" flexWrap="wrap" gap={2}>
                  <FormControl sx={{ minWidth: 150 }} size="small">
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={filter}
                      label="Status"
                      onChange={(e) => setFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="late">Late</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl sx={{ minWidth: 200 }} size="small">
                    <InputLabel id="property-filter-label">Property</InputLabel>
                    <Select
                      labelId="property-filter-label"
                      value={property}
                      label="Property"
                      onChange={(e) => setProperty(e.target.value)}
                    >
                      <MenuItem value="all">All Properties</MenuItem>
                      <MenuItem value="Oakwood Apartments">Oakwood Apartments</MenuItem>
                      <MenuItem value="Parkview Residences">Parkview Residences</MenuItem>
                      <MenuItem value="Sunset Heights">Sunset Heights</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    size="small"
                    label="Search Tenant"
                    variant="outlined"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                </ArgonBox>
                
                {/* Table */}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tenant</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Property</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payment</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar 
                                sx={{ 
                                  bgcolor: row.avatarColor, 
                                  width: 35, 
                                  height: 35,
                                  mr: 1.5 
                                }}
                              >
                                {row.tenant.split(' ').map(name => name[0]).join('')}
                              </Avatar>
                              {row.tenant}
                            </Box>
                          </TableCell>
                          <TableCell>{row.unit}</TableCell>
                          <TableCell>{row.property}</TableCell>
                          <TableCell>${row.amount}</TableCell>
                          <TableCell>{new Date(row.dueDate).toLocaleDateString()}</TableCell>
                          <TableCell>{getStatusChip(row.status)}</TableCell>
                          <TableCell>
                            {row.paidOn ? (
                              <Stack direction="column" spacing={0}>
                                <Typography variant="body2">
                                  {new Date(row.paidOn).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.method}
                                </Typography>
                              </Stack>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleMenuClick(e, row)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Table pagination or "showing x of y entries" would go here */}
                <ArgonBox mt={2} display="flex" justifyContent="flex-end">
                  <ArgonTypography variant="body2" color="text">
                    Showing {filteredData.length} of {rentData.length} entries
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      
      {/* Action menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleAction('view')}>View Details</MenuItem>
        <MenuItem onClick={() => handleAction('record')}>Record Payment</MenuItem>
        <MenuItem onClick={() => handleAction('remind')}>Send Reminder</MenuItem>
        <MenuItem onClick={() => handleAction('history')}>Payment History</MenuItem>
      </Menu>
      
      <Footer />
    </DashboardLayout>
  );
}

export default RentCollection;